import React, { useState, useCallback, useRef, useEffect } from 'react';
import CompositeAddress from '../CompositeAddress/CompositeAddress';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

// ─── Types ────────────────────────────────────────────────────────────────────

type BusinessAddressValue = {
  business_address_line1?: string;
  business_address_line2?: string;
  business_address_line3?: string;
  business_address_landmark?: string;
  business_address_pincode?: string;
  registered_address_line1?: string;
  registered_address_line2?: string;
  registered_address_line3?: string;
  registered_address_landmark?: string;
  registered_address_pincode?: string;
  [key: string]: string | undefined;
};

interface BusinessAddressDetailsProps {
  form?: any;
  value?: BusinessAddressValue;
  onChange?: (val: BusinessAddressValue) => void;
  disabled?: boolean;
  readOnly?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const BusinessAddressDetails: React.FC<BusinessAddressDetailsProps> = ({
  form,
  value = {},
  onChange,
  disabled = false,
  readOnly = false,
  registerSubmit,
}) => {
  const [sameAsBusiness, setSameAsBusiness] = useState(false);

  // Hold child submit handlers registered by each CompositeAddress
  const childHandlersRef = useRef<Record<string, () => Promise<boolean>>>({});

  // Register a submit handler that runs all child validators
  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      const handlers = Object.values(childHandlersRef.current);
      for (const handler of handlers) {
        const ok = await handler();
        if (!ok) return false;
      }
      return true;
    });
  }, [registerSubmit]);

  // Copy business → registered fields in the current value object
  const mirrorBusinessToRegistered = useCallback(
    (source: BusinessAddressValue): BusinessAddressValue => ({
      ...source,
      registered_address_line1: source.business_address_line1 ?? '',
      registered_address_line2: source.business_address_line2 ?? '',
      registered_address_line3: source.business_address_line3 ?? '',
      registered_address_landmark: source.business_address_landmark ?? '',
      registered_address_pincode: source.business_address_pincode ?? '',
    }),
    []
  );

  const handleSameAsBusinessChange = useCallback(
    (checked: boolean) => {
      setSameAsBusiness(checked);

      if (!checked) return;

      if (form) {
        const options = { shouldValidate: true, shouldDirty: true };
        form.setValue('registered_address_line1', form.getValues('business_address_line1') || '', options);
        form.setValue('registered_address_line2', form.getValues('business_address_line2') || '', options);
        form.setValue('registered_address_line3', form.getValues('business_address_line3') || '', options);
        form.setValue('registered_address_landmark', form.getValues('business_address_landmark') || '', options);
        form.setValue('registered_address_pincode', form.getValues('business_address_pincode') || '', options);
      } else if (onChange) {
        onChange(mirrorBusinessToRegistered(value));
      }
    },
    [form, value, onChange, mirrorBusinessToRegistered]
  );

  // Called when the Business Address sub-form changes.
  // If checkbox is on, keep registered in sync automatically.
  const handleBusinessChange = useCallback(
    (updated: BusinessAddressValue) => {
      if (!onChange) return;
      onChange(sameAsBusiness ? mirrorBusinessToRegistered(updated) : updated);
    },
    [onChange, sameAsBusiness, mirrorBusinessToRegistered]
  );

  // Called when the Registered Address sub-form changes (only when checkbox is off).
  const handleRegisteredChange = useCallback(
    (updated: BusinessAddressValue) => {
      onChange?.(updated);
    },
    [onChange]
  );

  // The effective value shown in the Registered Address section.
  // When checkbox is on, mirror business fields into registered keys.
  const effectiveValue: BusinessAddressValue = sameAsBusiness
    ? mirrorBusinessToRegistered(value)
    : value;

  return (
    <div className="space-y-6">
      {/* ── Business Address ─────────────────────────────────────────────── */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b pb-1">
          Business Address
        </h3>
        <CompositeAddress
          form={form}
          prefix="business"
          value={value}
          onChange={handleBusinessChange}
          disabled={disabled}
          readOnly={readOnly}
          registerSubmit={(handler) => { childHandlersRef.current['business'] = handler; }}
        />
      </div>

      {/* ── "Same as Business Address" checkbox ──────────────────────────── */}
      <div className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <Checkbox
          id="same-as-business"
          checked={sameAsBusiness}
          onCheckedChange={(checked) => handleSameAsBusinessChange(Boolean(checked))}
          disabled={disabled || readOnly}
        />
        <Label
          htmlFor="same-as-business"
          className="text-sm font-medium text-gray-700 cursor-pointer select-none"
        >
          Registered address is same as Business address
        </Label>
      </div>

      {/* ── Registered Address ───────────────────────────────────────────── */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b pb-1">
          Registered Address
        </h3>
        <CompositeAddress
          form={form}
          prefix="registered"
          value={effectiveValue}
          onChange={handleRegisteredChange}
          disabled={disabled || sameAsBusiness}
          readOnly={readOnly || sameAsBusiness}
          registerSubmit={sameAsBusiness ? undefined : (handler) => { childHandlersRef.current['registered'] = handler; }}
        />
      </div>
    </div>
  );
};

export default BusinessAddressDetails;
